import { useState, useEffect } from 'react';
import { message } from 'antd';
import { flowService } from '../services/flowService';
import { myAssistantServices } from '../services/myAssistantServices';
import { Flow, FlowConversation, FlowAction, FlowState, FlowMessage, FlowStatus, ProcessFlow } from '../types';

import { createLogger } from '../../../utils/logger';
const logger = createLogger('features-assistants');

export const useWorkflows = (assistantId: string) => {
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [executions, setExecutions] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [executionsLoading, setExecutionsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      setWorkflows([]);
      const data = await flowService.fetchProcessFlowCards(assistantId);
      setWorkflows(data);
      setError(null);
    } catch (err) {
      logger.error('Error fetching workflows:', err);
      setError('Failed to fetch workflows');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (assistantId) {
      fetchWorkflows();
    }
  }, [assistantId]);

  const fetchExecutions = async (workflowId: string) => {
    try {
      setExecutionsLoading(true);
      const response = await myAssistantServices.getWorkflowExecutions(workflowId);
      if (response.success && response.data) {
        setExecutions(response.data.executions || []);
      } else {
        setExecutions([]);
      }
    } catch (err) {
      logger.error('Error fetching executions:', err);
      setError('Failed to fetch executions');
      setExecutions([]);
    } finally {
      setExecutionsLoading(false);
    }
  };

  const handleActivateWorkflow = async (workflowId: string) => {
    try {
      const updatedWorkflow = await flowService.activateWorkflow(workflowId);
      setWorkflows(prev => prev.map(w => w.id === workflowId ? updatedWorkflow : w));
      message.success('Workflow activated');
    } catch (error) {
      message.error('Failed to activate workflow');
    }
  };

  const handleDeactivateWorkflow = async (workflowId: string) => {
    try {
      const updatedWorkflow = await flowService.deactivateWorkflow(workflowId);
      setWorkflows(prev => prev.map(w => w.id === workflowId ? updatedWorkflow : w));
      message.success('Workflow deactivated');
    } catch (error) {
      message.error('Failed to deactivate workflow');
    }
  };

  const handleDeleteWorkflow = async (workflowId: string) => {
    try {
      await flowService.deleteProcessFlow(workflowId);
      setWorkflows(prev => prev.filter(w => w.id !== workflowId));
      message.success('Workflow deleted');
    } catch (error) {
      message.error('Failed to delete workflow');
    }
  };

  return {
    workflows,
    executions,
    loading,
    executionsLoading,
    error,
    fetchExecutions,
    fetchWorkflows,
    handleActivateWorkflow,
    handleDeactivateWorkflow,
    handleDeleteWorkflow,
  };
}; 